import React, { useRef, useState } from 'react';
import {
    Box,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    IconButton, Tooltip, Typography, Paper, Checkbox, Button, TextField, Stack,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import dayjs from 'dayjs';
import { BackendPayload, FieldDef, FormFieldType } from '../../types/forms';
import ConfirmModal from '../ConfirmModal/ConfirmModal';
import useFormulaRunner from '../../hooks/useFormulaRunner';

type RowId = string | number;
export type FormResult = {
                             id: RowId;
                             userEmail: string;
                             date: string;
                         } & Record<string, any>;

type FormsResultsTableProps = {
    config: BackendPayload;
    data: FormResult[];
    onEdit?: (id: RowId) => void;
    onDelete?: (id: RowId) => void;
    onDeleteSelected?: (ids: RowId[]) => void;
    isRemoveLoading?: boolean;
    maxHeight?: number | string;
    placeholder?: string;
    hasActions?: boolean;
};

const ACTIONS_W = 100;
const ID_W = 60;
const CHECK_W = 46;
const MIDDLE_W = 180;

const isEmptyLike = (v: any) =>
    v === null || v === undefined || v === '' || (
        Array.isArray(v) && v.length === 0
    );

const fmt = (raw: any, type: FormFieldType) => {
    if (type === 'date') {
        const d = dayjs(raw);
        return d.isValid() ? d.format('h:mma MM/DD/YY').toUpperCase() : String(raw ?? '');
    }
    if (Array.isArray(raw) || typeof raw === 'object') {
        return JSON.stringify(raw ?? '');
    }
    return String(raw ?? '');
};

const formatValue = (type: FormFieldType, raw: any, placeholder: string): React.ReactNode => {
    if (isEmptyLike(raw)) {
        return <Typography variant="body2" color="text.disabled">{placeholder}</Typography>;
    }
    if (type === 'date') {
        const d = dayjs(raw);
        return d.isValid() ? d.format('h:mma MM/DD/YY').toUpperCase() : String(raw);
    }
    if (type === 'color') {
        const v = String(raw);
        return (
            <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}>
                <Box sx={{
                    width: 16, height: 16, borderRadius: 0.5, border: '1px solid rgba(0,0,0,0.2)', background: v,
                }}/>
                <Typography variant="body2">{v}</Typography>
            </Box>
        );
    }
    if (Array.isArray(raw)) {
        return raw.length ? JSON.stringify(raw) : placeholder;
    }
    if (typeof raw === 'object') {
        return Object.keys(raw).length ? JSON.stringify(raw) : placeholder;
    }
    return String(raw);
};

const FormsResultsTable: React.FC<FormsResultsTableProps> = ({
    config,
    data,
    onEdit,
    onDelete,
    onDeleteSelected,
    maxHeight = 'unset',
    placeholder = '-',
    hasActions = false,
    isRemoveLoading = false,
}) => {
    const formulaRunner = useFormulaRunner();
    const [ confirmModalOpen, setConfirmModalOpen ] = useState(false);
    const responseIdsToRemove = useRef<(string | number)[]>([]);

    const fieldMap = React.useMemo(() => {
        const map = new Map<string, FieldDef>();
        config.fields.forEach(f => map.set(f.id, f));
        return map;
    }, [ config.fields ]);

    const columns = React.useMemo(() => config.fields.map(f => f.id), [ config.fields ]);

    const [ selected, setSelected ] = React.useState<Set<RowId>>(new Set());
    const allIds = React.useMemo(() => data.map(r => r.id), [ data ]);
    const allSelected = selected.size > 0 && selected.size === allIds.length;
    const someSelected = selected.size > 0 && selected.size < allIds.length;

    const toggleAll = () => setSelected(allSelected ? new Set() : new Set(allIds));
    const toggleOne = (id: RowId) => {
        setSelected(prev => {
            const next = new Set(prev);
            next.has(id) ? next.delete(id) : next.add(id);
            return next;
        });
    };
    const clearSelection = () => setSelected(new Set());
    const handleDeleteSelected = () => {
        if (onDeleteSelected && selected.size > 0) {
            setConfirmModalOpen(true);
            responseIdsToRemove.current = Array.from(selected);
        }
    };
    const onDeleteConfirm = () => {
        onDeleteSelected?.(responseIdsToRemove.current);
        clearSelection();
    };

    const [ query, setQuery ] = React.useState('');
    const q = query.trim().toLowerCase();

    const filtered = React.useMemo(() => {
        if (!q) {
            return data;
        }
        return data.filter(row => {
            const hay = [
                String(row.id ?? ''),
                String(row.userEmail ?? ''),
                dayjs(row.date).isValid() ? dayjs(row.date).format('h:mma MM/DD/YY')
                                                           .toUpperCase() : String(row.date ?? ''),
                ...columns.map(col => {
                    const def = fieldMap.get(col);
                    const type: FormFieldType = def?.type ?? 'text';
                    return fmt((
                        row as any
                    )[col], type);
                }),
            ]
            .join(' | ')
            .toLowerCase();
            return hay.includes(q);
        });
    }, [ q, data, columns, fieldMap ]);

    const getFormulaResult = (formula: string, data: any): string => {
        const mergedData = {};
        Object.keys(data).forEach(key => {
            // @ts-ignore
            mergedData[key] = { value: data[key], points: 1 };
        });
        const result = formulaRunner(formula, mergedData);
        console.log(result);
        return result as string;
    };

    return (
        <>
            <Paper sx={{ border: 1, borderColor: 'divider' }}>
                <Box sx={{ px: 2, py: 1, borderBottom: 1, borderColor: 'divider' }}>
                    <Stack direction="row" spacing={1} alignItems="center">
                        <TextField
                            size="small"
                            placeholder="Search all fields…"
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            sx={{ width: 340 }}
                            type="search"
                        />
                    </Stack>
                </Box>

                <TableContainer
                    sx={{
                        width: '100%',
                        overflowX: 'auto',
                        overflowY: 'auto',
                        maxHeight,
                        '& .MuiTableCell-stickyHeader': { bgcolor: '#013b71', color: 'primary.contrastText' },
                    }}
                >
                    <Table
                        size="small"
                        stickyHeader
                        sx={{
                            tableLayout: 'fixed',
                            borderCollapse: 'separate',
                            '& th, & td': { borderBottom: 1, borderColor: 'divider' },
                        }}
                    >
                        <colgroup>
                            <col style={{ width: CHECK_W }}/>
                            <col style={{ width: ID_W }}/>
                            {columns.map((_, i) => (
                                <col key={`mid-${i}`} style={{ width: MIDDLE_W }}/>
                            ))}
                            <col style={{ width: hasActions ? ACTIONS_W : 0 }}/>
                        </colgroup>

                        <TableHead>
                            <TableRow sx={{ '& th': { py: 0.5, fontSize: '0.85rem', backgroundColor: '#013b71' } }}>
                                <TableCell
                                    sx={{
                                        position: 'sticky',
                                        left: 0,
                                        zIndex: 4,
                                        bgcolor: '#013b71',
                                    }}
                                >
                                    <Checkbox
                                        checked={allSelected}
                                        indeterminate={someSelected}
                                        onChange={toggleAll}
                                        size="small"
                                        sx={{
                                            color: 'primary.contrastText',
                                            '&.Mui-checked': { color: 'primary.contrastText' },
                                            '& .MuiSvgIcon-root': { fill: 'white' },
                                        }}
                                    />
                                </TableCell>

                                <TableCell
                                    sx={{
                                        position: 'sticky',
                                        left: CHECK_W,
                                        zIndex: 3,
                                        bgcolor: '#013b71',
                                        color: 'white',
                                        whiteSpace: 'nowrap',
                                        fontWeight: 700,
                                    }}
                                >
                                    Id
                                </TableCell>

                                {columns.map(col => {
                                    const def = fieldMap.get(col);
                                    const label = def?.label ?? col;
                                    return (
                                        <TableCell
                                            key={col}
                                            title={label}
                                            sx={{
                                                bgcolor: '#013b71',
                                                color: 'white',
                                                fontWeight: 700,
                                                whiteSpace: 'nowrap',
                                            }}
                                        >
                                            {label}
                                        </TableCell>
                                    );
                                })}

                                {hasActions && (
                                    <TableCell
                                        align="right"
                                        sx={{
                                            position: 'sticky',
                                            right: 0,
                                            zIndex: 3,
                                            bgcolor: '#013b71',
                                            color: 'white',
                                            whiteSpace: 'nowrap',
                                            fontWeight: 700,
                                        }}
                                    >
                                        Actions
                                    </TableCell>
                                )}
                            </TableRow>
                        </TableHead>

                        <TableBody>
                            {filtered.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={columns.length + 5}>
                                        <Typography variant="body2" color="text.secondary">No results</Typography>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                filtered.map(row => {
                                    const checked = selected.has(row.id);
                                    return (
                                        <TableRow
                                            key={row.id}
                                            sx={{
                                                '&:hover td': { backgroundColor: '#f0f6fb' },
                                            }}
                                        >
                                            <TableCell
                                                sx={{
                                                    position: 'sticky',
                                                    left: 0,
                                                    zIndex: 2,
                                                    bgcolor: 'background.paper',
                                                }}
                                            >
                                                <Checkbox checked={checked} onChange={() => toggleOne(row.id)}
                                                          size="small"/>
                                            </TableCell>

                                            <TableCell
                                                sx={{
                                                    position: 'sticky',
                                                    left: CHECK_W,
                                                    zIndex: 2,
                                                    bgcolor: 'background.paper',
                                                    whiteSpace: 'nowrap',
                                                    color: 'text.secondary',
                                                }}
                                            >
                                                {row.id}
                                            </TableCell>

                                            {columns.map(col => {
                                                const def = fieldMap.get(col);
                                                const type: FormFieldType = def?.type ?? 'text';
                                                let val = (
                                                    row as any
                                                )[col];

                                                if (type === 'formula') {
                                                    return (
                                                        <TableCell
                                                            key={`${row.id}-${col}`}
                                                            sx={{
                                                                bgcolor: 'background.paper',
                                                                textTransform: 'capitalize',
                                                            }}
                                                        >
                                                            {getFormulaResult(def?.formula || '', row)}
                                                        </TableCell>
                                                    );
                                                }

                                                return (
                                                    <TableCell
                                                        key={`${row.id}-${col}`}
                                                        sx={{
                                                            bgcolor: 'background.paper',
                                                            textTransform: 'capitalize',
                                                        }}
                                                    >
                                                        {formatValue(type, val, placeholder)}
                                                    </TableCell>
                                                );
                                            })}

                                            {hasActions && (
                                                <TableCell
                                                    align="right"
                                                    sx={{
                                                        position: 'sticky',
                                                        right: 0,
                                                        zIndex: 2,
                                                        bgcolor: 'background.paper',
                                                    }}
                                                >
                                                    {!!onEdit && (
                                                        <Tooltip title="Edit">
                            <span>
                              <IconButton size="small" onClick={() => onEdit(row.id)} disabled={!onEdit}>
                                <EditIcon fontSize="small" style={{ color: '#013b71' }}/>
                              </IconButton>
                            </span>
                                                        </Tooltip>
                                                    )}
                                                    {!!onDelete && (
                                                        <Tooltip title="Delete">
                            <span>
                              <IconButton size="small" color="error" onClick={() => onDelete(row.id)}
                                          disabled={!onDelete}>
                                <DeleteIcon fontSize="small"/>
                              </IconButton>
                            </span>
                                                        </Tooltip>
                                                    )}
                                                </TableCell>
                                            )}
                                        </TableRow>
                                    );
                                })
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>

                {selected.size > 0 && hasActions && (
                    <Box
                        sx={{
                            bottom: 0,
                            bgcolor: 'background.paper',
                            borderTop: 1,
                            borderColor: 'divider',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            px: 2,
                            py: 1,
                        }}
                    >
                        <Typography variant="body2" color="text.secondary">
                            {selected.size} selected
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                            <Button variant="text" onClick={clearSelection}>Unselect all</Button>
                            <Button
                                startIcon={<DeleteIcon fontSize="small"/>}
                                variant="contained"
                                color="error"
                                onClick={handleDeleteSelected}
                                disabled={!onDeleteSelected}
                            >
                                Delete selected
                            </Button>
                        </Box>
                    </Box>
                )}
            </Paper>
            <ConfirmModal
                isLoading={isRemoveLoading}
                open={confirmModalOpen}
                title="Are you sure?"
                message="You will not be able to restore the removed data!"
                onConfirm={onDeleteConfirm}
                onCancel={() => setConfirmModalOpen(false)}
            />
        </>
    );
};

export default FormsResultsTable;